import React, { useState } from 'react';
import { Card, Button, Form, Badge, Dropdown } from 'react-bootstrap';
import '../styles/queriesManagement.css';

const QueryDetails = ({ query, onClose, onReply, onAssign, onResolve, onReopen, teams, readOnly = false }) => {
    const [replyMessage, setReplyMessage] = useState('');
    const [showReplyForm, setShowReplyForm] = useState(false);
    const [sending, setSending] = useState(false);

    if (!query) return null;

    const handleSendReply = async () => {
        if (!replyMessage.trim()) {
            return;
        }

        setSending(true);
        await onReply(query._id, replyMessage);
        setSending(false);
        setReplyMessage('');
        setShowReplyForm(false);
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        const variants = {
            pending: 'warning',
            assigned: 'info',
            resolved: 'success'
        };
        return <Badge bg={variants[status] || 'secondary'}>{status || 'pending'}</Badge>;
    };

    return (
        <Card className="query-details-card">
            <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Query Details</h5>
                <Button variant="link" onClick={onClose} className="text-decoration-none text-white">
                    ✕
                </Button>
            </Card.Header>
            <Card.Body>
                {/* Query Header */}
                <div className="query-header mb-4">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                            <h6 className="mb-2">{query.subject || 'No Subject'}</h6>
                            <div className="text-muted mb-2">
                                <strong>From:</strong> {query.from?.name || query.from?.email || 'Unknown'}
                                <br />
                                <strong>Email:</strong> {query.from?.email || 'No email'}
                                <br />
                                <strong>Received:</strong> {formatDateTime(query.receivedAt)}
                            </div>
                        </div>
                        <div className="text-end">
                            {getStatusBadge(query.status)}
                            {query.assignedTo && (
                                <div className="mt-2">
                                    <Badge bg="secondary">
                                        Assigned to: {query.assignedTo}
                                    </Badge>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Action Buttons — hidden in archive/read-only mode */}
                    {!readOnly && (
                        <div className="d-flex gap-2 mb-3">
                            <Dropdown>
                                <Dropdown.Toggle variant="outline-primary" size="sm">
                                    Assign to Team
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {teams.map(team => (
                                        <Dropdown.Item
                                            key={team}
                                            onClick={() => onAssign(query._id, team)}
                                        >
                                            {team.charAt(0).toUpperCase() + team.slice(1)}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>

                            <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => setShowReplyForm(!showReplyForm)}
                            >
                                Reply
                            </Button>

                            {query.status !== 'resolved' ? (
                                <Button
                                    variant="outline-info"
                                    size="sm"
                                    onClick={() => onResolve(query._id)}
                                >
                                    Mark Resolved
                                </Button>
                            ) : (
                                onReopen && (
                                    <Button
                                        variant="outline-warning"
                                        size="sm"
                                        onClick={() => onReopen(query._id)}
                                    >
                                        Mark Pending
                                    </Button>
                                )
                            )}
                        </div>
                    )}

                    {/* Reply Form */}
                    {!readOnly && showReplyForm && (
                        <Card className="mb-3 bg-light">
                            <Card.Body>
                                <Form.Group className="mb-3">
                                    <Form.Label>Your Reply</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={5}
                                        value={replyMessage}
                                        onChange={(e) => setReplyMessage(e.target.value)}
                                        placeholder="Type your reply here..."
                                    />
                                </Form.Group>
                                <div className="d-flex gap-2">
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={handleSendReply}
                                        disabled={sending || !replyMessage.trim()}
                                    >
                                        {sending ? 'Sending...' : 'Send Reply'}
                                    </Button>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => {
                                            setShowReplyForm(false);
                                            setReplyMessage('');
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    )}
                </div>

                {/* Original Message */}
                <div className="query-thread">
                    <h6 className="mb-3">Conversation Thread</h6>
                    
                    <Card className="mb-3 message-card">
                        <Card.Body>
                            <div className="d-flex justify-content-between mb-2">
                                <strong>{query.from?.name || query.from?.email || 'Unknown Sender'}</strong>
                                <small className="text-muted">
                                    {formatDateTime(query.receivedAt)}
                                </small>
                            </div>
                            <div className="message-body" style={{ whiteSpace: 'pre-wrap' }}>
                                {query.body || 'No message content'}
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Replies */}
                    {query.replies && query.replies.length > 0 && (
                        <>
                            <h6 className="mb-3 mt-4">Replies ({query.replies.length})</h6>
                            {query.replies.map((reply, index) => (
                                <Card key={index} className="mb-3 message-card reply-card">
                                    <Card.Body>
                                        <div className="d-flex justify-content-between mb-2">
                                            <div>
                                                <strong className="text-primary">
                                                    {reply.from || 'spats.iitkgp@gmail.com'}
                                                </strong>
                                                {reply.to && (
                                                    <>
                                                        <span className="mx-2">→</span>
                                                        <span className="text-muted">{reply.to}</span>
                                                    </>
                                                )}
                                            </div>
                                            <small className="text-muted">
                                                {formatDateTime(reply.sentAt)}
                                            </small>
                                        </div>
                                        <div className="message-body" style={{ whiteSpace: 'pre-wrap' }}>
                                            {reply.message || 'No message'}
                                        </div>
                                    </Card.Body>
                                </Card>
                            ))}
                        </>
                    )}
                </div>

                {/* Additional Info */}
                {(query.assignedAt || query.resolvedAt) && (
                    <Card className="mt-4 bg-light">
                        <Card.Body>
                            <h6 className="mb-3">Activity Timeline</h6>
                            {query.assignedAt && (
                                <div className="mb-2">
                                    <small className="text-muted">
                                        <strong>Assigned:</strong> {formatDateTime(query.assignedAt)}
                                        {query.assignedBy && ` by ${query.assignedBy}`}
                                    </small>
                                </div>
                            )}
                            {query.resolvedAt && (
                                <div className="mb-2">
                                    <small className="text-muted">
                                        <strong>Resolved:</strong> {formatDateTime(query.resolvedAt)}
                                        {query.resolvedBy && ` by ${query.resolvedBy}`}
                                    </small>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                )}
            </Card.Body>
        </Card>
    );
};

export default QueryDetails;
